import { useCallback, useRef, useEffect } from 'react';
import type { TransactionNavigationInfo } from '../components/TransactionViewModal';

interface UseModalNavigationParams<T extends { id: string }> {
  /** The currently viewed transaction */
  viewingTransaction: T | null;
  /** The list of transactions visible on the current page */
  transactions: T[];
  /** Callback to set the viewed transaction */
  setViewingTransaction: (tx: T) => void;
  /** Total number of items across all pages */
  totalItems: number;
  /** Current zero-based page index */
  currentPage: number;
  /** Items per page */
  pageSize: number;
  /** Total number of pages */
  totalPages: number;
  /** Callback to change the page */
  setCurrentPage: (page: number) => void;
  /** When non-empty, navigation is scoped to these IDs (in order) instead of
   *  the paginated full list. Pagination is skipped — navigation walks the
   *  array and emits stub transactions ({ id }) when an id isn't on the
   *  current page; the parent's URL/data sync fetches the full record. */
  selectedIds?: string[];
}

/**
 * Hook that provides prev/next navigation for the TransactionViewModal.
 * Handles cross-page navigation automatically — when the user navigates
 * past the current page, the page changes and the hook picks up the
 * first/last transaction on the new page once data loads.
 */
export function useModalNavigation<T extends { id: string }>({
  viewingTransaction,
  transactions,
  setViewingTransaction,
  totalItems,
  currentPage,
  pageSize,
  totalPages,
  setCurrentPage,
  selectedIds,
}: UseModalNavigationParams<T>): TransactionNavigationInfo {
  const pendingIndexRef = useRef<number | null>(null);

  const isSelectedScope = (selectedIds?.length ?? 0) > 0;

  /** Page-aware "go next, then prev, then end" used by both the pager arrows
   *  (when direction is fixed) and post-action advancement. Returns whether
   *  the move landed on a target. Cross-page moves return 'advanced' as soon
   *  as setCurrentPage is called — the pendingIndexRef effect resolves the
   *  target once new data loads. */
  const movePage = useCallback(
    (direction: 'prev' | 'next' | 'auto'): 'advanced' | 'end' => {
      const currentIdx = viewingTransaction
        ? transactions.findIndex((t) => t.id === viewingTransaction.id)
        : -1;

      const tryNext = direction === 'next' || direction === 'auto';
      const tryPrev = direction === 'prev' || direction === 'auto';

      if (tryNext) {
        if (currentIdx >= 0 && currentIdx + 1 < transactions.length) {
          setViewingTransaction(transactions[currentIdx + 1]);
          return 'advanced';
        }
        if (
          (currentIdx === -1 || currentIdx + 1 >= transactions.length) &&
          currentPage < totalPages - 1
        ) {
          pendingIndexRef.current = 0;
          setCurrentPage(currentPage + 1);
          return 'advanced';
        }
      }

      if (tryPrev) {
        if (currentIdx > 0) {
          setViewingTransaction(transactions[currentIdx - 1]);
          return 'advanced';
        }
        if (currentIdx <= 0 && currentPage > 0) {
          pendingIndexRef.current = pageSize - 1;
          setCurrentPage(currentPage - 1);
          return 'advanced';
        }
      }

      return 'end';
    },
    [
      viewingTransaction,
      transactions,
      currentPage,
      totalPages,
      pageSize,
      setViewingTransaction,
      setCurrentPage,
    ],
  );

  /** Selection-scope sibling of `movePage`. */
  const moveSelected = useCallback(
    (direction: 'prev' | 'next' | 'auto'): 'advanced' | 'end' => {
      if (!isSelectedScope || !selectedIds) return 'end';
      const list = selectedIds;
      const currentIdx = viewingTransaction
        ? list.indexOf(viewingTransaction.id)
        : -1;

      // Out of scope: any move pulls the user into the selected set.
      if (currentIdx === -1) {
        const target = list[0];
        const onPage = transactions.find((t) => t.id === target);
        setViewingTransaction(onPage ?? ({ id: target } as T));
        return 'advanced';
      }

      const tryNext = direction === 'next' || direction === 'auto';
      const tryPrev = direction === 'prev' || direction === 'auto';

      if (tryNext && currentIdx + 1 < list.length) {
        const target = list[currentIdx + 1];
        const onPage = transactions.find((t) => t.id === target);
        setViewingTransaction(onPage ?? ({ id: target } as T));
        return 'advanced';
      }

      if (tryPrev && currentIdx > 0) {
        const target = list[currentIdx - 1];
        const onPage = transactions.find((t) => t.id === target);
        setViewingTransaction(onPage ?? ({ id: target } as T));
        return 'advanced';
      }

      return 'end';
    },
    [
      isSelectedScope,
      selectedIds,
      viewingTransaction,
      transactions,
      setViewingTransaction,
    ],
  );

  const navigate = useCallback(
    (direction: 'prev' | 'next') => {
      if (isSelectedScope) moveSelected(direction);
      else movePage(direction);
    },
    [isSelectedScope, moveSelected, movePage],
  );

  /** Page-callers invoke this after a successful mutation: tries next, falls
   *  back to prev, returns 'end' when there's nowhere to go (caller closes). */
  const navigateAfterAction = useCallback(
    (): 'advanced' | 'end' =>
      isSelectedScope ? moveSelected('auto') : movePage('auto'),
    [isSelectedScope, moveSelected, movePage],
  );

  // After a cross-page navigation, resolve the pending index once new data loads.
  useEffect(() => {
    if (pendingIndexRef.current != null && transactions.length > 0) {
      const idx = Math.min(pendingIndexRef.current, transactions.length - 1);
      pendingIndexRef.current = null;
      setViewingTransaction(transactions[idx]);
    }
  }, [transactions, setViewingTransaction]);

  if (isSelectedScope && selectedIds) {
    // -1 when the open row isn't part of the selection — the modal renders an
    // out-of-scope state ("—/N") so the position display isn't misleading.
    const idx = viewingTransaction
      ? selectedIds.indexOf(viewingTransaction.id)
      : -1;
    return {
      currentIndex: idx,
      totalItems: selectedIds.length,
      onNavigate: navigate,
      navigateAfterAction,
    };
  }

  const viewingIndex = viewingTransaction
    ? transactions.findIndex((t) => t.id === viewingTransaction.id)
    : -1;

  return {
    currentIndex: viewingIndex >= 0 ? currentPage * pageSize + viewingIndex : 0,
    totalItems,
    onNavigate: navigate,
    navigateAfterAction,
  };
}
